import { useCallback } from 'react'

import { getFavoriteToastActions } from '../../../components/global/favorites/FavoriteToastActions'
import { useUniqueToast } from '../../toasts/hooks/useUniqueToast'

export interface ShowRemovedToastOptions {
  productName?: string | null
  onUndo?: () => void
}

export interface UseFavoriteToast {
  showRemovedToast: (options?: ShowRemovedToastOptions) => void
  showErrorToast: (productName?: string | null) => void
}

const getRemovedMessage = (productName?: string | null): string =>
  productName ? `${productName} removed from Favorites` : 'Removed from Favorites'

const getErrorMessage = (productName?: string | null): string =>
  productName
    ? `We couldn't update ${productName}. Please try again.`
    : `We couldn't update your Favorites. Please try again.`

export const useFavoriteToast = (): UseFavoriteToast => {
  const { showToast, hideToastById } = useUniqueToast({ toastIdPrefix: 'favorite-' })

  const showRemovedToast = useCallback(
    ({ productName, onUndo }: ShowRemovedToastOptions = {}) => {
      showToast(getRemovedMessage(productName), {
        variant: 'success',
        action: getFavoriteToastActions({ onUndo, dismissToast: hideToastById }),
        style: { flexWrap: 'nowrap', alignItems: 'center' },
      })
    },
    [showToast, hideToastById],
  )

  const showErrorToast = useCallback(
    (productName?: string | null) => {
      showToast(getErrorMessage(productName), {
        variant: 'error',
        action: getFavoriteToastActions({ dismissToast: hideToastById }),
        style: { flexWrap: 'nowrap', alignItems: 'center' },
      })
    },
    [showToast, hideToastById],
  )

  return { showRemovedToast, showErrorToast }
}
